import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, PauseCircle, Send } from "lucide-react";

/**
 * Collections, escalation and the pause on a client's project.
 *
 * This lived on the Client Tracker page, where it was the only finance-gated
 * card among ten production ones — it rendered for nobody but finance, and a
 * PM's job on that page is dates and deliverables. It belongs beside the money
 * it is about, so it lives on the Payments tab now and fetches its own tracker.
 *
 * Finance-only in both directions. Pausing a client's project is a decision
 * with a relationship attached, and lifting it should be done by whoever can
 * actually see the money arrive — not by the PM watching their own project sit
 * still.
 */
export default function TrackerCollectionsCard({ programId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/project-tracker/program/${programId}`);
      setData(res.data);
    } catch {
      // A project with no tracker, or a user who can't manage one, simply
      // doesn't get this card. Not an error worth a toast on the Payments tab.
      setData(null);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const payments = data.payments || {};
  const hold = payments.hold || {};
  const assessment = payments.hold_assessment || {};
  const billing = payments.billing || {};
  const banner = data.banner;

  if (!data.user_is_finance || !payments.has_schedule) return null;

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      await load();
      toast.success(okMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't do that");
    } finally {
      setBusy(false);
    }
  };

  const setBilling = (patch, msg) =>
    call(() => apiClient.patch(`/project-tracker/program/${programId}/billing`, patch), msg);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <PauseCircle className="w-4 h-4" /> Collections and pause
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* What the client is reading right now. Nobody should discover the
            wording of a final notice by hearing about it from the client. */}
        {banner && (
          <div className="rounded-xl border p-4 bg-muted/40">
            <p className="text-xs text-muted-foreground mb-1.5">
              Word for word, what the client is reading at the top of their dashboard right now
            </p>
            <p className="text-sm font-medium">{banner.title}</p>
            <p className="text-sm text-muted-foreground mt-1">{banner.body}</p>
          </div>
        )}

        {hold.active ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-900">This project is paused for non-payment</p>
            <p className="text-sm text-red-800 mt-1">
              Paused {hold.since_text || "recently"}
              {hold.by_name ? ` by ${hold.by_name}` : ""}
              {hold.reason ? ` — ${hold.reason}` : ""}.
              {" "}The client sees a pause banner on every page of their dashboard.
            </p>
            <Button size="sm" variant="outline" className="mt-3" disabled={busy}
              onClick={() => call(
                () => apiClient.post(`/project-tracker/program/${programId}/billing/release`, {}),
                "Project resumed",
              )}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Payment received — resume the project
            </Button>
          </div>
        ) : assessment.recommended ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-900">
              Past the pause threshold — {assessment.days_overdue} days overdue
              {assessment.total_overdue_text ? ` on ${assessment.total_overdue_text}` : ""}
            </p>
            <p className="text-sm text-amber-800 mt-1">
              {assessment.contractual_floor_passed
                ? "The agreement already allows suspension after 15 days past due, so we're within our rights."
                : "Below the contractual 15-day floor — worth a call before pausing."}
            </p>
            <Input className="mt-3 bg-card" value={reason} maxLength={160}
              placeholder="Reason (internal — the client sees a generic pause message)"
              onChange={(e) => setReason(e.target.value)} />
            <Button size="sm" variant="destructive" className="mt-2" disabled={busy}
              onClick={() => call(
                () => apiClient.post(`/project-tracker/program/${programId}/billing/hold`, {
                  reason, milestone_id: assessment.milestone_id, pause_stage: true,
                }),
                "Project paused and moved to On Hold",
              )}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Pause this project
            </Button>
          </div>
        ) : assessment.days_overdue > 0 ? (
          <div className="rounded-xl border p-4">
            <p className="text-sm">
              {assessment.days_overdue} days overdue
              {assessment.total_overdue_text ? ` on ${assessment.total_overdue_text}` : ""} —
              {" "}pause threshold in {assessment.days_until} days.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-green-200 bg-green-50 p-4">
            <p className="text-sm text-green-900">Nothing overdue on this project.</p>
          </div>
        )}

        <div className="rounded-xl border divide-y">
          <ToggleRow
            label="Late-payment interest"
            hint="1.5% per month on overdue balances, per the MSA. Shown to the client as accrued interest — never added to the ledger."
            on={!!billing.late_fees_enabled} busy={busy}
            onToggle={() => setBilling(
              { late_fees_enabled: !billing.late_fees_enabled },
              billing.late_fees_enabled ? "Interest hidden from the client" : "Interest now shown",
            )}
          />
          <ToggleRow
            label="Escalating reminder emails"
            hint="One email per stage, ever: 14 days before, on the day, then 3, 7, 14 and 21 days past due. Needs a client email on this tracker."
            on={!!billing.reminders_enabled} busy={busy}
            onToggle={() => setBilling(
              { payment_reminders_enabled: !billing.reminders_enabled },
              billing.reminders_enabled ? "Reminders off" : "Reminders on",
            )}
          />
          <ToggleRow
            label="Pause automatically at the threshold"
            hint="Off by default. With this on, the project pauses itself once the threshold passes — no one has to remember."
            on={!!billing.auto_hold_enabled} busy={busy}
            onToggle={() => setBilling(
              { auto_hold_enabled: !billing.auto_hold_enabled },
              billing.auto_hold_enabled ? "Automatic pause off" : "Automatic pause armed",
            )}
          />
          <div className="p-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm">Pause after</p>
              <p className="text-xs text-muted-foreground">
                Days past due before this project is eligible to pause. The agreement allows 15; we default to 21.
              </p>
            </div>
            <Input type="number" min={15} max={365} className="w-20 shrink-0"
              defaultValue={billing.hold_after_days}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (v && v !== billing.hold_after_days) setBilling({ hold_after_days: v }, "Threshold updated");
              }} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Run the escalation now — sends any reminder that's due and applies the automatic pause if it's armed.
          </p>
          <Button size="sm" variant="outline" className="shrink-0" disabled={busy}
            onClick={() => call(async () => {
              const res = await apiClient.post(`/project-tracker/program/${programId}/billing/evaluate`);
              const n = res.data.reminders_sent;
              toast.message(n ? `${n} reminder${n === 1 ? "" : "s"} sent` : "Nothing due right now");
            }, "Escalation run")}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
            Run now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, hint, on, busy, onToggle }) {
  return (
    <button onClick={onToggle} disabled={busy}
      className="p-3 flex items-start gap-3 text-left w-full">
      <span className={`w-8 rounded-full relative transition-colors shrink-0 mt-0.5 ${on ? "bg-green-600" : "bg-muted-foreground/30"}`}
        style={{ height: 18 }}>
        <span className="absolute top-0.5 w-3.5 h-3.5 rounded-full bg-[#FFFFFF] transition-all"
          style={{ left: on ? 16 : 2 }} />
      </span>
      <span className="text-sm min-w-0">
        <span className="font-medium block">{label}</span>
        <span className="text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}
